/**
 * @file firestoreService.ts
 * @fileoverview Generic service for Firestore database interactions.
 * @description Provides a set of reusable functions for common CRUD operations, queries, and pagination.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  type DocumentData,
  FieldValue,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  type QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  WithFieldValue,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebaseClient.config";
import { DEFAULT_LIST_LIMIT, withDefaultLimit } from "./queryBounds";

/**
 * 🔥 SDK BRIDGE
 * Exporting these functions and types ensures that all apps in the monorepo
 * use the exact same SDK instance and definitions. This prevents
 * "different Firestore SDK" runtime errors and TS2345 mismatches.
 */
export { where, orderBy, limit, startAfter };
export type { DocumentData, QueryDocumentSnapshot };

/**
 * Base interface for documents stored in Firestore, ensuring common fields.
 */
export interface CMSDocument extends DocumentData {
  id?: string;
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
}

/**
 * A generic service providing common data access patterns for Firestore.
 */
export const FirestoreService = {
  /**
   * Retrieves the most recent documents from a collection, ordered by creation
   * date and bounded by `max` (P2.2 — this used to read the whole collection).
   */
  getAll: async <T = CMSDocument>(
    collectionName: string,
    max: number = DEFAULT_LIST_LIMIT,
  ): Promise<T[]> => {
    const q = query(
      collection(db, collectionName),
      orderBy("createdAt", "desc"),
      limit(max),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  },

  /**
   * Retrieves a single document by its unique ID from a specified collection.
   * @returns A promise that resolves to the document data or `null` if not found.
   */
  getById: async <T = CMSDocument>(
    collectionName: string,
    id: string,
  ): Promise<T | null> => {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching doc ${id} from ${collectionName}:`, error);
      throw error;
    }
  },

  /**
   * Finds the first document that matches a specific field-value pair.
   * @example findOne('posts', 'slug', 'my-first-post');
   */
  findOne: async <T = CMSDocument>(
    collectionName: string,
    field: string,
    value: unknown,
  ): Promise<T | null> => {
    const q = query(
      collection(db, collectionName),
      where(field, "==", value),
      limit(1),
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as T;
    }
    return null;
  },

  /**
   * Executes a flexible query against a collection using a set of constraints.
   *
   * A `limit()` is appended when the caller supplied none, so no code path can
   * accidentally read an entire collection (P2.2).
   *
   * @param constraints A list of `QueryConstraint` objects (e.g., `where`, `orderBy`, `limit`).
   */
  query: async <T = CMSDocument>(
    collectionName: string,
    ...constraints: QueryConstraint[]
  ): Promise<T[]> => {
    const q = query(
      collection(db, collectionName),
      ...withDefaultLimit(constraints, limit),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  },

  /**
   * Fetches a paginated list of documents from a collection.
   * @param collectionName The name of the collection to query.
   * @param pageSize The number of documents to retrieve per page.
   * @param lastDoc The last `QueryDocumentSnapshot` from the previous page, used for cursor-based pagination.
   * @param additionalConstraints Optional `QueryConstraint`s to apply to the query.
   * @returns An object containing the page data, the last document for the next cursor, and a boolean indicating if more pages exist.
   */
  getPaginated: async <T = CMSDocument>(
    collectionName: string,
    pageSize: number = 10,
    lastDoc?: QueryDocumentSnapshot<DocumentData>,
    ...additionalConstraints: QueryConstraint[]
  ) => {
    const constraints: QueryConstraint[] = [
      orderBy("createdAt", "desc"),
      ...additionalConstraints,
      limit(pageSize),
    ];

    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    const q = query(collection(db, collectionName), ...constraints);
    const snapshot = await getDocs(q);

    return {
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as T[],
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      hasMore: snapshot.docs.length === pageSize,
    };
  },

  /**
   * Calculates the total number of documents in a collection that match the given query constraints.
   * @example getCount('users', where('active', '==', true));
   */
  getCount: async (
    collectionName: string,
    ...constraints: QueryConstraint[]
  ): Promise<number> => {
    const q = query(collection(db, collectionName), ...constraints);
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  },

  /**
   * Adds a new document to a collection with `createdAt` and `updatedAt` server timestamps.
   * @returns A promise that resolves with the `DocumentReference` of the newly created document.
   */
  add: async <T extends WithFieldValue<DocumentData>>(
    collectionName: string,
    data: T,
  ) => {
    const dataWithTimestamps = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    return await addDoc(collection(db, collectionName), dataWithTimestamps);
  },

  /**
   * Creates or overwrites a document with a specific ID.
   * Useful for user profiles where the document ID should match the user's UID.
   * @param collectionName The name of the collection.
   * @param id The document ID to use.
   * @param data The document data.
   * @param merge If true, merges with existing data instead of overwriting.
   */
  setWithId: async <T extends WithFieldValue<DocumentData>>(
    collectionName: string,
    id: string,
    data: T,
    merge: boolean = false,
  ) => {
    const docRef = doc(db, collectionName, id);
    const dataWithTimestamps = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    return await setDoc(docRef, dataWithTimestamps, { merge });
  },

  /**
   * Updates an existing document in a collection and sets the `updatedAt` server timestamp.
   */
  update: async <T extends DocumentData>(
    collectionName: string,
    id: string,
    data: Partial<T>,
  ) => {
    const docRef = doc(db, collectionName, id);
    const dataWithTimestamp = {
      ...data,
      updatedAt: serverTimestamp(),
    };
    return await updateDoc(docRef, dataWithTimestamp);
  },

  /**
   * Deletes a single document from a collection by its ID.
   */
  delete: async (collectionName: string, id: string) => {
    const docRef = doc(db, collectionName, id);
    return await deleteDoc(docRef);
  },

  /**
   * Deletes multiple documents from a collection in a single atomic operation.
   * @param ids An array of document IDs to delete.
   * @description This operation is limited to 500 writes per batch by Firestore.
   */
  deleteBatch: async (collectionName: string, ids: string[]) => {
    if (!ids.length) return;

    const batch = writeBatch(db);

    ids.forEach((id) => {
      const docRef = doc(db, collectionName, id);
      batch.delete(docRef);
    });

    return await batch.commit();
  },
};
